import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone, FileError } from 'react-dropzone';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import { useApi } from '../../context/Api';
import { AccountManagementDocuments, ClearerType, ResponseCodes } from '@tradingblock/types';
import { ApiResponse } from '@tradingblock/api';
import { Text } from '@tradingblock/components';
import jsPDF from 'jspdf';

/**
 * MiscellaneousUpload component properties
 *
 * @property {number} accountId Account ID Associated With File Upload
 * @property {void} handleDocumentUpload Following successful upload, passes the documentId to parent component
 * @property {AccountManagementDocuments} documentType Upload document type
 * @property {string} description Description of the document
 */

interface MiscellaneousUploadProps {
  accountId: number;
  handleDocumentUpload: (payload: number, accountHolderIndex: number, documentType: AccountManagementDocuments) => void;
  documentType: AccountManagementDocuments;
  description: string;
  accountHolderIndex: number;
  isCashieringDocument?: boolean;
  recipientBankId?: number;
  disabled?: boolean;
  clearer?: ClearerType;
}

/**
 *  Upload widget for miscellaneous documents related to account management updates (3210 letter, government ids, signatures, etc.)
 *
 *  @param {MiscellaneousUploadProps} props
 *  @returns {JSX.Element}
 */

export const MiscUploadFileField: React.FC<MiscellaneousUploadProps> = ({
  accountId,
  handleDocumentUpload,
  documentType,
  description,
  accountHolderIndex,
  isCashieringDocument,
  recipientBankId,
  disabled,
  clearer,
}) => {
  // Used Hooks
  const api = useApi();

  // Used States
  const [uploadingDocument, setUploadingDocument] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<FileError[]>([]);
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  // Used States for ID Card
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [frontImgPreview, setFrontImgPreview] = useState<string | null>(null);
  const [backImgPreview, setBackImgPreview] = useState<string | null>(null);
  const isIdCard =
    documentType === AccountManagementDocuments.DriversLicense ||
    documentType === AccountManagementDocuments.StateIdCard;

  const renderImageToCanvas = async (imageSrc: any) => {
    const img = new Image();
    img.src = imageSrc;
    await new Promise(resolve => (img.onload = resolve));
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0);
    }
    return canvas;
  };

  const generatePDF = async () => {
    try {
      const pdf = new jsPDF();

      // Render front image to canvas
      const frontCanvas = await renderImageToCanvas(frontImgPreview);
      const frontImgData = frontCanvas.toDataURL('image/jpeg');
      const frontImgWidth = frontCanvas.width;
      const frontImgHeight = frontCanvas.height;

      // Maintain aspect ratio for front image
      const frontAspectRatio = frontImgWidth / frontImgHeight;
      const frontNewWidth = 150; // Desired width
      const frontNewHeight = frontNewWidth / frontAspectRatio;

      pdf.addImage(frontImgData, 'JPEG', 15, 40, frontNewWidth, frontNewHeight);

      // Add a new page
      pdf.addPage();
      // Render back image to canvas
      const backCanvas = await renderImageToCanvas(backImgPreview);
      const backImgData = backCanvas.toDataURL('image/jpeg');
      const backImgWidth = backCanvas.width;
      const backImgHeight = backCanvas.height;

      // Maintain aspect ratio for back image
      const backAspectRatio = backImgWidth / backImgHeight;
      const backNewWidth = 150; // Desired width
      const backNewHeight = backNewWidth / backAspectRatio;

      pdf.addImage(backImgData, 'JPEG', 15, 40, backNewWidth, backNewHeight);

      // Generate PDF Blob and display
      const pdfBlob = pdf.output('blob');

      // check size of the pdfBlob
      if (pdfBlob.size > maxFileSize) {
        alert('The combined image is too large to upload. Please try again with smaller images.');
        return;
      }

      // return a new file object with the pdfBlob
      return new File([pdf.output('blob')], 'combined.pdf', { type: 'application/pdf' });
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  const handleCombinedUpload = async () => {
    if (frontImage && backImage) {
      setUploadingDocument(true);
      try {
        const fileToUpload = await generatePDF();

        if (fileToUpload) {
          if (!isCashieringDocument) {
            api.accountManagement
              .uploadMiscellaneousDocument(fileToUpload, accountId, documentType, description)
              .then(response => {
                processUploadResponse(response);
              });
          } else if (isCashieringDocument && recipientBankId) {
            api.cashiering.documents
              .upload(fileToUpload, accountId, recipientBankId, documentType, description)
              .then(response => {
                processUploadResponse(response);
              });
          }
        } else {
          throw new Error('File to upload is undefined.');
        }
      } catch (error) {
        setErrors([{ message: 'An error occurred while processing the upload.', code: 'UPLOAD_ERROR' }]);
        setUploadingDocument(false);
      }
    }
  };

  // Process upload api response
  const processUploadResponse = useCallback((response: ApiResponse<number>) => {
    // If unsuccessful upload, set state/errors
    if (response.responseCode !== 0) {
      setUploadingDocument(false);
      setErrors([
        {
          message: ResponseCodes[response.responseCode].description as string,
          code: ResponseCodes[response.responseCode].name,
        },
      ]);
    } else {
      // If successful, set states and pass documentId to parent for update submission
      setUploadSuccess(true);
      handleDocumentUpload(response.payload, accountHolderIndex, documentType);
      setErrors([]);
    }
  }, []);

  // Handle image drop for ID card
  const handleImageDrop = (side: 'front' | 'back') => async (acceptedFiles: File[]) => {
    // if the side is front, set the front image state
    // if the side is back, set the back image state
    const file = acceptedFiles[0];
    // if both the front and back images are set, combine them into a single PDF and upload automatically without user intervention
    if (side === 'front') {
      setFrontImgPreview(URL.createObjectURL(file));
      setFrontImage(file);
    } else if (side === 'back') {
      setBackImgPreview(URL.createObjectURL(file));
      setBackImage(file);
    }
  };

  // Dropzone Properties
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: () => {
      setUploadingDocument(true);
    },
    onDropAccepted: acceptedFiles => {
      // If drop accepted by frontend, upload the file to backend
      setFileName(acceptedFiles[0].name);
      if (!isCashieringDocument) {
        api.accountManagement
          .uploadMiscellaneousDocument(acceptedFiles[0], accountId, documentType, description)
          .then(response => {
            processUploadResponse(response);
          });
      } else if (isCashieringDocument && recipientBankId) {
        api.cashiering.documents
          .upload(acceptedFiles[0], accountId, recipientBankId, documentType, description)
          .then(response => {
            processUploadResponse(response);
          });
      }
    },
    // If drop rejected, set states/errors
    onDropRejected: fileRejections => {
      setUploadingDocument(false);
      setErrors(fileRejections[0].errors);
    },
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, application/pdf, capture=camera',
  });

  // Dropzones for ID Card
  const { getRootProps: getFrontRootProps, getInputProps: getFrontInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('front')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, capture=camera',
  });

  const { getRootProps: getBackRootProps, getInputProps: getBackInputProps } = useDropzone({
    onDrop: acceptedFiles => handleImageDrop('back')(acceptedFiles),
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: 'image/png, image/jpeg, image/jpg, image/gif, capture=camera',
  });

  const formatDocumentName = (documentName: string) => {
    const specialCases: { [key: string]: string } = {
      DriversLicense: "Driver's License",
      StateIdCard: 'State ID Card',
      Passport: 'Passport',
      MilitaryId: 'Military ID',
      SsnCard: 'SSN Card',
      SsaLetter: 'SSA Letter',
      IrsItinLetter: 'IRS ITIN Letter',
      OtherGovernmentId: 'Other Government ID',
      CddDocument: 'CDD Document',
      AllPassingCipResults: 'All Passing CIP Results',
      IrsW9Form: 'IRS W-9 Form',
      SignatureImage: 'Signature Image',
      IdDocument: 'ID Document',
      AffiliateApproval: 'Affiliate Approval',
      Document: 'Document',
      Other: 'Other',
      GovernmentId: 'Government ID',
    };

    if (specialCases[documentName]) {
      return specialCases[documentName];
    }

    return (
      documentName
        // Add spaces before each capital letter except the first one
        .replace(/([A-Z])/g, ' $1')
        // Trim any leading spaces
        .trim()
        // Convert to title case
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
    );
  };

  useEffect(() => {
    if (frontImage && backImage) {
      handleCombinedUpload();
    }
  }, [frontImage, backImage]);

  return (
    <>
      {!uploadSuccess ? (
        <>
          {!isIdCard && (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              {!uploadingDocument ? (
                <>
                  <Button disabled={disabled}>
                    <i className="far fa-paperclip" /> Upload {formatDocumentName(description)}
                  </Button>
                </>
              ) : (
                <span>
                  <i className="fas fa-circle-notch fa-spin valid" />{' '}
                  <Text id={'investigationUpload.uploading'} type={'field'} />
                </span>
              )}
            </div>
          )}
          {isIdCard && (
            <>
              {/* Only display if there is no frontImage state currently set*/}
              {frontImage === null && (
                <div {...getFrontRootProps()}>
                  <input {...getFrontInputProps()} />
                  <Button disabled={disabled}>
                    <i className="far fa-paperclip" /> Upload {formatDocumentName(description)} - Front
                  </Button>
                </div>
              )}
              {/* Only display if there is no backImage state currently set*/}
              {backImage === null && (
                <div {...getBackRootProps()}>
                  <input {...getBackInputProps()} />
                  <Button disabled={disabled}>
                    <i className="far fa-paperclip" /> Upload {formatDocumentName(description)} - Back
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <br />
          <span>
            <i className="fas fa-check-circle text-success" /> File successfully uploaded - {fileName}
          </span>
          <br />
        </>
      )}
      {errors.length > 0 && (
        <div className="errors">
          <ul>
            {errors.map((error, errorIndex) => (
              <li className="error" key={`error_${errorIndex}`}>
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};
